import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { qk } from '@/lib/query-keys';
import { clientsService, type ClientListParams } from '@/services/clients.service';
import { categoriesService, equipmentService, type EquipmentListParams } from '@/services/equipment.service';
import { ordersService, type OrderListParams } from '@/services/orders.service';
import { productsService, servicesService, type CatalogListParams } from '@/services/catalog.service';
import { componentTypesService, componentsService, orderComponentsService, stockService } from '@/services/components.service';
import { financeService } from '@/services/finance.service';
import { dashboardService, settingsService, usersService } from '@/services/misc.service';

export const useClientsList = (params: ClientListParams) =>
  useQuery({
    queryKey: qk.clients.list(params),
    queryFn: ({ signal }) => clientsService.list(params, signal),
    placeholderData: keepPreviousData,
  });

export const useClient = (id?: string) =>
  useQuery({ queryKey: qk.clients.detail(id ?? ''), queryFn: () => clientsService.get(id!), enabled: !!id });

export const useClientEquipment = (id?: string, includeInactive = false) =>
  useQuery({
    queryKey: [...qk.clients.equipment(id ?? ''), includeInactive],
    queryFn: () => clientsService.equipment(id!, includeInactive),
    enabled: !!id,
  });

export const useClientOrders = (id?: string) =>
  useQuery({ queryKey: qk.clients.orders(id ?? ''), queryFn: () => clientsService.orders(id!), enabled: !!id });

export const useClientMaintenance = (id?: string) =>
  useQuery({
    queryKey: qk.clients.maintenance(id ?? ''),
    queryFn: () => clientsService.maintenance(id!),
    enabled: !!id,
  });

export const useCategories = (includeInactive = false) =>
  useQuery({
    queryKey: qk.categories.list(includeInactive),
    queryFn: () => categoriesService.list(includeInactive),
    staleTime: 5 * 60_000,
  });

export const useOrderChecklist = (orderId?: string | null) =>
  useQuery({ queryKey: qk.orders.checklist(orderId ?? ''), queryFn: () => ordersService.checklist(orderId!), enabled: !!orderId });

export const useEquipmentList = (params: EquipmentListParams) =>
  useQuery({
    queryKey: qk.equipment.list(params),
    queryFn: ({ signal }) => equipmentService.list(params, signal),
    placeholderData: keepPreviousData,
  });

export const useEquipmentHistory = (id?: string | null) =>
  useQuery({ queryKey: qk.equipment.history(id ?? ''), queryFn: () => equipmentService.history(id!), enabled: !!id });

export const useOrdersList = (params: OrderListParams) =>
  useQuery({
    queryKey: qk.orders.list(params),
    queryFn: ({ signal }) => ordersService.list(params, signal),
    placeholderData: keepPreviousData,
  });

export const useBench = () =>
  useQuery({ queryKey: qk.orders.bench, queryFn: ({ signal }) => ordersService.bench(signal), refetchInterval: 60_000 });

export const useOrder = (id?: string) =>
  useQuery({ queryKey: qk.orders.detail(id ?? ''), queryFn: () => ordersService.get(id!), enabled: !!id });

export const useProducts = (params: CatalogListParams = {}, enabled = true) =>
  useQuery({
    queryKey: qk.products.list(params),
    queryFn: ({ signal }) => productsService.list(params, signal),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useServices = (params: CatalogListParams = {}, enabled = true) =>
  useQuery({
    queryKey: qk.services.list(params),
    queryFn: ({ signal }) => servicesService.list(params, signal),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useTechnicians = () =>
  useQuery({ queryKey: qk.users.technicians, queryFn: usersService.technicians, staleTime: 5 * 60_000 });

export const useUsers = () => useQuery({ queryKey: qk.users.all, queryFn: () => usersService.list(true) });

export const useDashboard = () =>
  useQuery({ queryKey: qk.dashboard, queryFn: dashboardService.summary, refetchInterval: 60_000 });

export const useCompanySettings = () =>
  useQuery({ queryKey: qk.settings, queryFn: settingsService.get, staleTime: 5 * 60_000 });

export const useComponents = (params: CatalogListParams = {}) =>
  useQuery({
    queryKey: qk.components.list(params),
    queryFn: ({ signal }) => componentsService.list(params, signal),
    placeholderData: keepPreviousData,
  });

export const useOrderComponents = (orderId?: string) =>
  useQuery({
    queryKey: qk.orders.components(orderId ?? ''),
    queryFn: () => orderComponentsService.list(orderId!),
    enabled: !!orderId,
  });

export const useFinanceOverview = (month: string) =>
  useQuery({
    queryKey: qk.finance.overview(month),
    queryFn: () => financeService.overview(month),
    placeholderData: keepPreviousData,
  });

export const useFinanceReceivables = () =>
  useQuery({ queryKey: qk.finance.receivables, queryFn: financeService.receivables });

export const useStockOverview = () =>
  useQuery({
    queryKey: qk.stock.overview,
    queryFn: stockService.overview,
  });

export const useComponentTypes = (includeInactive = false) =>
  useQuery({
    queryKey: qk.componentTypes.list(includeInactive),
    queryFn: () => componentTypesService.list(includeInactive),
    staleTime: 60_000,
  });
